/**
 * Required-field messages that name the fields actually missing.
 *
 * The forms used to guard with `if (!code || !name) toast("Code and name are
 * required")`, which names both fields whichever one is blank — so an admin who
 * filled the name and forgot the code was told the name was missing too, and
 * had to work out which field the form meant.
 */

export interface RequiredField {
  /** As it reads on the form, so the message points at something visible. */
  label: string;
  value: string | null | undefined;
}

/** Labels of the fields that are empty or whitespace-only, in form order. */
export function missingRequired(fields: RequiredField[]): string[] {
  return fields.filter((f) => !f.value || !f.value.trim()).map((f) => f.label);
}

/**
 * "Employee code is required" · "Employee code and Full name are required" ·
 * "A, B and C are required". Null when nothing is missing, so callers can use
 * it directly as the guard.
 */
export function requiredMessage(missing: string[]): string | null {
  if (missing.length === 0) return null;
  if (missing.length === 1) return `${missing[0]} is required`;
  const head = missing.slice(0, -1).join(", ");
  return `${head} and ${missing[missing.length - 1]} are required`;
}

/** `missingRequired` + `requiredMessage` — the whole guard in one call. */
export function validateRequired(fields: RequiredField[]): string | null {
  return requiredMessage(missingRequired(fields));
}
